'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface SignUpFormData {
  name: string;
  email: string;
  password: string;
  artistType: string;
  terms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const onSubmit = async (data: SignUpFormData) => {
    setLoading(true);
    // Backend integration: POST /api/auth/signup with { name, email, password, artistType }
    await new Promise((r) => setTimeout(r, 1400));
    toast.success(`Account created! Welcome to TrackLab, ${data.name}.`);
    router.push('/projects-dashboard');
  };

  const handleGoogle = async () => {
    // Backend integration: initiate Google OAuth signup flow
    toast.info('Redirecting to Google…');
    await new Promise((r) => setTimeout(r, 800));
    router.push('/projects-dashboard');
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-1">Start creating</h2>
      <p className="text-sm text-gray-400 mb-8">Free account — no credit card required</p>

      {/* Google button */}
      <button
        onClick={handleGoogle}
        className="w-full flex items-center justify-center gap-3 bg-dark-card border border-dark-border rounded-xl py-3 text-sm font-medium text-gray-200 hover:border-brand-violet/40 hover:bg-dark-elevated transition-all duration-200 mb-6 active:scale-[0.98]"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-dark-border" />
        <span className="text-xs text-gray-600">or</span>
        <div className="flex-1 h-px bg-dark-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Artist / Stage name</label>
          <input
            type="text"
            {...register('name', { required: 'Artist name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
            placeholder="Nova Ryze"
            className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-violet/60 focus:ring-1 focus:ring-brand-violet/30 transition-all duration-200"
          />
          {errors.name && <p className="text-xs text-red-400 mt-1.5">{errors.name.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Email address</label>
          <input
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
            })}
            placeholder="nova@yourband.com"
            className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-violet/60 focus:ring-1 focus:ring-brand-violet/30 transition-all duration-200"
          />
          {errors.email && <p className="text-xs text-red-400 mt-1.5">{errors.email.message}</p>}
        </div>

        {/* Artist type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">I am a…</label>
          <select
            {...register('artistType', { required: 'Select your artist type' })}
            className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-brand-violet/60 focus:ring-1 focus:ring-brand-violet/30 transition-all duration-200 appearance-none"
          >
            <option value="">Select type…</option>
            <option value="solo">Solo Artist</option>
            <option value="band">Band / Group</option>
            <option value="producer">Music Producer</option>
            <option value="songwriter">Songwriter</option>
            <option value="other">Other</option>
          </select>
          {errors.artistType && <p className="text-xs text-red-400 mt-1.5">{errors.artistType.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
          <p className="text-xs text-gray-500 mb-1.5">At least 8 characters</p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              placeholder="••••••••"
              className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-violet/60 focus:ring-1 focus:ring-brand-violet/30 transition-all duration-200"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-400 mt-1.5">{errors.password.message}</p>}
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="terms"
            {...register('terms', { required: 'You must agree to the terms' })}
            className="w-4 h-4 mt-0.5 rounded border-dark-border bg-dark-card accent-brand-violet cursor-pointer flex-shrink-0"
          />
          <label htmlFor="terms" className="text-xs text-gray-400 cursor-pointer select-none leading-relaxed">
            I agree to the{' '}
            <span className="text-brand-violet hover:text-brand-violet-light cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-brand-violet hover:text-brand-violet-light cursor-pointer">Privacy Policy</span>
          </label>
        </div>
        {errors.terms && <p className="text-xs text-red-400 -mt-2">{errors.terms.message}</p>}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-brand-violet hover:bg-brand-violet-dark text-white font-semibold py-3 rounded-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-neon-violet"
          style={{ minHeight: '48px' }}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Creating account…</span>
            </>
          ) : (
            'Create Free Account'
          )}
        </button>
      </form>

      <p className="text-sm text-gray-500 text-center mt-6">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-brand-violet hover:text-brand-violet-light transition-colors font-medium">
          Sign in
        </button>
      </p>
    </div>
  );
}